import React, { useCallback, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Upload, FileText, ImageIcon, X, AlertCircle } from 'lucide-react';
import {
  TIPO_DOCUMENTO_OPTIONS, TipoDocumento, ACCEPTED_EXT, MAX_BYTES,
  validateFile, type PendingEvidencia,
} from '@/hooks/useQualityEvidencias';
import { cn } from '@/lib/utils';

interface Props {
  value: PendingEvidencia[];
  onChange: (next: PendingEvidencia[]) => void;
  disabled?: boolean;
}

const fmtSize = (b: number) =>
  b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

const fileIcon = (mime: string) =>
  mime.startsWith('image/') ? <ImageIcon className="h-4 w-4 text-primary" /> : <FileText className="h-4 w-4 text-primary" />;

export function QualityEvidenceUpload({ value, onChange, disabled }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const addFiles = useCallback(
    (files: FileList | File[]) => {
      const next: PendingEvidencia[] = [...value];
      Array.from(files).forEach((file) => {
        const err = validateFile(file);
        next.push({
          id: crypto.randomUUID(),
          file,
          tipo_documento: 'evidencia_complementar',
          descricao: '',
          error: err || undefined,
        });
      });
      onChange(next);
    },
    [value, onChange],
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled) return;
      if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
    },
    [addFiles, disabled],
  );

  const remove = (id: string) => onChange(value.filter((v) => v.id !== id));
  const patch = (id: string, p: Partial<PendingEvidencia>) =>
    onChange(value.map((v) => (v.id === id ? { ...v, ...p } : v)));

  const hasErrors = value.some((v) => v.error);

  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium">
        Anexos / Evidências{' '}
        <span className="text-muted-foreground font-normal">
          (PDF, JPG, PNG · máx. {Math.round(MAX_BYTES / 1024 / 1024)} MB cada)
        </span>
      </Label>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        className={cn(
          'border-2 border-dashed rounded-lg px-4 py-5 text-center cursor-pointer transition-colors',
          dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/40',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
      >
        <Upload className="h-5 w-5 mx-auto text-muted-foreground mb-1.5" />
        <p className="text-xs font-medium">
          Arraste arquivos aqui ou <span className="text-primary underline">selecione</span>
        </p>
        <p className="text-[11px] text-muted-foreground mt-0.5">Múltiplos arquivos suportados</p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED_EXT}
          className="hidden"
          disabled={disabled}
          onChange={(e) => {
            if (e.target.files?.length) addFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {value.length > 0 && (
        <div className="space-y-1.5 max-h-[260px] overflow-y-auto pr-1">
          {value.map((p) => (
            <div
              key={p.id}
              className={cn(
                'border rounded-md p-2 bg-muted/30',
                p.error && 'border-destructive/50 bg-destructive/5',
              )}
            >
              <div className="flex items-start gap-2">
                <div className="mt-0.5">{fileIcon(p.file.type)}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-medium truncate">{p.file.name}</p>
                    <Badge variant="outline" className="text-[10px] py-0 h-4 shrink-0">
                      {fmtSize(p.file.size)}
                    </Badge>
                  </div>
                  {p.error ? (
                    <p className="text-[11px] text-destructive flex items-center gap-1 mt-1">
                      <AlertCircle className="h-3 w-3" /> {p.error}
                    </p>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-1.5 mt-1.5">
                      <Select
                        value={p.tipo_documento}
                        onValueChange={(v) => patch(p.id, { tipo_documento: v as TipoDocumento })}
                        disabled={disabled}
                      >
                        <SelectTrigger className="h-7 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIPO_DOCUMENTO_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value} className="text-xs">
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="Descrição (opcional)"
                        value={p.descricao || ''}
                        onChange={(e) => patch(p.id, { descricao: e.target.value })}
                        disabled={disabled}
                        className="h-7 text-xs"
                        maxLength={200}
                      />
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    remove(p.id);
                  }}
                  disabled={disabled}
                  aria-label="Remover"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {hasErrors && (
        <p className="text-[11px] text-destructive">
          Remova ou corrija os arquivos inválidos antes de enviar.
        </p>
      )}
    </div>
  );
}
